import { FC, useState, useCallback, useMemo, memo, useRef, useEffect } from 'react';
import { KatButton, KatFileUpload, KatInput } from '@amzn/katal-react';
import { MaterialRadioButtonGroup } from 'src/components/general/MaterialRadioButtonGroup';
import { SearchImageInput } from 'src/components/pages/campaignAutomation/input/assets/SearchImageInput';
import { useToast } from 'src/contexts/ToastContext';
import { validateUrl } from 'src/utils/assetInput/assetInputUtils';
import { validateImageDimensions } from 'src/utils/validations/Images';
import { DimensionSpecs } from 'src/utils/Types';
import { ErrorConstants } from 'src/constants';
import { formatFileSize } from 'src/utils/fileUtils';
import { fetchUrlMetadata } from 'src/helpers';
import { validateAssetMetadata } from 'src/utils/ValidationUtils';
import styles from './MultiImageInput.module.css';

type ImageInputMethod = 'upload' | 'url' | 'search';

// Validates an image file against the provided spec.
export const validateImageFile = async (
  file: File,
  validationSpec: ImageValidationSpec,
): Promise<string | null> => {
  if (file.size > validationSpec.maxFileSizeBytes) {
    return `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : Size ${formatFileSize(file.size)} exceeds the maximum file size limit ${formatFileSize(validationSpec.maxFileSizeBytes)}`;
  }
  try {
    const dimensionSpecs: DimensionSpecs = {
      minResolution: { width: validationSpec.minWidth, height: validationSpec.minHeight },
      ...(validationSpec.aspectRatio && {
        aspectRatio: validationSpec.aspectRatio,
      }),
    };
    await validateImageDimensions(file, dimensionSpecs);
    return null;
  } catch (error) {
    return error instanceof Error
      ? error.message
      : 'Unable to read image. Please upload a valid image file.';
  }
};

// Processes file attachment errors from KatFileUpload.
export const processFileAttachedError = (
  errorDetail: { error: string; files?: Array<{ file?: { name?: string; size?: number } }> },
  validationSpec: ImageValidationSpec,
): string => {
  const fileInfo = errorDetail.files?.[0]?.file;
  switch (errorDetail.error) {
    case 'kat_over_max_size':
      return `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : Size ${fileInfo?.size ? formatFileSize(fileInfo.size) : 'unknown'} exceeds the maximum file size limit ${formatFileSize(validationSpec.maxFileSizeBytes)}`;
    case 'kat_unsupported_file_attached':
      return `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Allowed types: ${validationSpec.acceptedFileTypes}`;
    default:
      return errorDetail.error || 'File upload error';
  }
};

export interface ImageEntry {
  file?: File;
  url?: string;
  name: string;
  preview: string;
  id: string;
}

export interface ImageValidationSpec {
  maxFileSizeBytes: number;
  minWidth: number;
  minHeight: number;
  aspectRatio?: { width: number; height: number };
  acceptedFileTypes: string;
}

export interface MultiImageInputProps {
  images: ImageEntry[];
  onImagesChange: (images: ImageEntry[]) => void;
  maxImages: number;
  validationSpec: ImageValidationSpec;
  disabled?: boolean;
  reorderHint?: string;
  'data-testid'?: string;
}

const INPUT_OPTIONS = [
  { value: 'search', label: 'Search' },
  { value: 'upload', label: 'Upload' },
  { value: 'url', label: 'URL' },
];

interface ImageRowProps {
  img: ImageEntry;
  idx: number;
  isDragging: boolean;
  isDisabled: boolean;
  onDragStart: (idx: number) => void;
  onDragOver: (e: React.DragEvent, idx: number) => void;
  onDragEnd: () => void;
  onRemove: (idx: number) => void;
}

const ImageRow = memo<ImageRowProps>(
  ({ img, idx, isDragging, isDisabled, onDragStart, onDragOver, onDragEnd, onRemove }) => (
    <div
      className={`${styles.imageCard} ${isDragging ? styles.dragging : ''} ${isDisabled ? styles.imageCardDisabled : ''}`}
      draggable={!isDisabled}
      onDragStart={() => !isDisabled && onDragStart(idx)}
      onDragOver={(e) => !isDisabled && onDragOver(e, idx)}
      onDrop={(e) => e.preventDefault()}
      onDragEnd={onDragEnd}
    >
      <span className={styles.imageIndex}>{idx + 1}</span>
      <img src={img.preview} alt={img.name} className={styles.imagePreview} />
      <span className={styles.imageName}>{img.name}</span>
      <button
        type="button"
        className={styles.removeImageButton}
        onClick={() => onRemove(idx)}
        disabled={isDisabled}
        title="Remove image"
      >
        ✕
      </button>
    </div>
  ),
);

export const MultiImageInput: FC<MultiImageInputProps> = ({
  images,
  onImagesChange,
  maxImages,
  validationSpec,
  disabled = false,
  reorderHint,
  'data-testid': dataTestId = 'multi-image-input',
}) => {
  const [inputMethod, setInputMethodRaw] = useState<ImageInputMethod>('search');
  const setInputMethod = useCallback((method: ImageInputMethod) => {
    setInputMethodRaw(method);
    setImageError(undefined);
  }, []);
  const [urlValue, setUrlValue] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [showImageInput, setShowImageInput] = useState(false);
  const [imageError, setImageError] = useState<string | undefined>();
  const uploadRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // Hide Katal's internal file list
  useEffect(() => {
    const container = uploadRef.current;
    if (!container) return;
    const katEl = container.querySelector('kat-file-upload');
    if (!katEl) return;

    const removeFileItems = () => {
      container.querySelectorAll('kat-file-item').forEach((el) => el.remove());
      if (katEl.shadowRoot) {
        katEl.shadowRoot
          .querySelectorAll('kat-file-item, [class*="file-item"], [class*="file-list"]')
          .forEach((el) => {
            (el as HTMLElement).style.display = 'none';
          });
      }
    };

    const observer = new MutationObserver(removeFileItems);
    observer.observe(container, { childList: true, subtree: true });
    if (katEl.shadowRoot) {
      observer.observe(katEl.shadowRoot, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, [inputMethod]);

  const validateImage = (file: File) => validateImageFile(file, validationSpec);

  // Handles Katal file upload errors (e.g., unsupported file type on drag-and-drop).
  // Note: kat_over_max_size is kept as defensive code in case maxSize is re-added to KatFileUpload.
  const handleFileAttachedError = (e: KatFileUpload.FilesAttachedErrorEvent) => {
    const message = processFileAttachedError(e.detail, validationSpec);
    setImageError(message);
    toast.addToast({ description: message, variant: 'danger' });
  };

  const handleFileAttached = async (e: KatFileUpload.FilesAttachedEvent) => {
    const files = Array.from(e.detail?.files || []);
    const validEntries: ImageEntry[] = [];
    const errors: string[] = [];

    for (const f of files) {
      const error = await validateImage(f);
      if (error) {
        errors.push(error);
        toast.addToast({ description: error, variant: 'danger' });
        continue;
      }
      const key = `${f.name}-${f.size}-${f.lastModified}`;
      validEntries.push({
        file: f,
        name: f.name,
        preview: URL.createObjectURL(f),
        id: key,
      });
    }

    setImageError(errors.length > 0 ? errors.join('; ') : undefined);

    if (validEntries.length > 0) {
      const existingKeys = new Set(images.map((img) => img.id));
      const newEntries = validEntries.filter((entry) => !existingKeys.has(entry.id));
      // Revoke blob URLs for filtered-out duplicates
      validEntries
        .filter((entry) => existingKeys.has(entry.id))
        .forEach((entry) => {
          if (entry.preview?.startsWith('blob:')) URL.revokeObjectURL(entry.preview);
        });
      const combined = [...images, ...newEntries];
      const kept = combined.slice(0, maxImages);
      // Revoke blob URLs for entries exceeding maxImages
      combined.slice(maxImages).forEach((entry) => {
        if (entry.preview?.startsWith('blob:')) URL.revokeObjectURL(entry.preview);
      });
      onImagesChange(kept);
      setShowImageInput(false);
    }
  };

  const isValidUrl = useMemo(() => validateUrl(urlValue), [urlValue]);
  const [isFetching, setIsFetching] = useState(false);

  const handleUrlAdd = async () => {
    if (!isValidUrl) return;
    const url = urlValue.trim();
    setIsFetching(true);
    setImageError(undefined);

    try {
      // Validate URL metadata (content-type + size) using shared utils
      const headers = await fetchUrlMetadata(url);
      const allowedTypes = validationSpec.acceptedFileTypes.split(',').map((t) => {
        const ext = t.replace('.', '').toLowerCase();
        return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      });
      validateAssetMetadata(headers, {
        maxSize: validationSpec.maxFileSizeBytes,
        allowedTypes,
      });

      // Fetch the actual image for dimension validation
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(
          `${ErrorConstants.MESSAGES.HTTP_ERROR}: { status: ${response.status} - ${response.statusText} }`,
        );
      }
      const blob = await response.blob();
      const fileName = new URL(url).pathname.split('/').pop() || 'image';
      const file = new File([blob], fileName, { type: blob.type });

      // Validate dimensions using the shared utility
      const dimensionSpecs: DimensionSpecs = {
        minResolution: { width: validationSpec.minWidth, height: validationSpec.minHeight },
        ...(validationSpec.aspectRatio && { aspectRatio: validationSpec.aspectRatio }),
      };
      await validateImageDimensions(file, dimensionSpecs);

      onImagesChange(
        [...images, { url, name: fileName, preview: url, id: crypto.randomUUID() }].slice(
          0,
          maxImages,
        ),
      );
      setUrlValue('');
      setShowImageInput(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch image from URL.';
      setImageError(message);
      toast.addToast({ description: message, variant: 'danger' });
    } finally {
      setIsFetching(false);
    }
  };

  const handleSearchSelect = async (imageUrl: string, imageTitle?: string) => {
    if (!validateUrl(imageUrl)) {
      setImageError('Invalid image URL from search results.');
      toast.addToast({ description: 'Invalid image URL from search results.', variant: 'danger' });
      return;
    }
    setIsFetching(true);
    setImageError(undefined);

    try {
      // Validate metadata (content-type + size)
      const headers = await fetchUrlMetadata(imageUrl);
      const allowedTypes = validationSpec.acceptedFileTypes.split(',').map((t) => {
        const ext = t.replace('.', '').toLowerCase();
        return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      });
      validateAssetMetadata(headers, {
        maxSize: validationSpec.maxFileSizeBytes,
        allowedTypes,
      });

      // Fetch actual image for dimension validation
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(
          `${ErrorConstants.MESSAGES.HTTP_ERROR}: { status: ${response.status} - ${response.statusText} }`,
        );
      }
      const blob = await response.blob();
      const mimeType = blob.type || 'image/jpeg';
      const ext = mimeType.split('/')[1]?.replace('jpeg', 'jpg') || 'jpg';
      const baseName = (
        imageTitle ||
        new URL(imageUrl).pathname.split('/').pop() ||
        'search-image'
      ).replace(/\.[^.]+$/, ''); // strip existing extension if any
      const fileName = `${baseName}.${ext}`;
      const file = new File([blob], fileName, { type: mimeType });

      // Validate dimensions (min resolution + aspect ratio)
      const dimensionSpecs: DimensionSpecs = {
        minResolution: { width: validationSpec.minWidth, height: validationSpec.minHeight },
        ...(validationSpec.aspectRatio && { aspectRatio: validationSpec.aspectRatio }),
      };
      await validateImageDimensions(file, dimensionSpecs);

      onImagesChange(
        [
          ...images,
          {
            file,
            url: imageUrl,
            name: fileName,
            preview: imageUrl,
            id: crypto.randomUUID(),
          },
        ].slice(0, maxImages),
      );
      setShowImageInput(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Search image failed validation.';
      setImageError(message);
      toast.addToast({ description: message, variant: 'danger' });
    } finally {
      setIsFetching(false);
    }
  };

  const handleRemoveImage = useCallback(
    (idx: number) => {
      const removed = images[idx];
      if (removed?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.preview);
      }
      onImagesChange(images.filter((_, i) => i !== idx));
    },
    [images, onImagesChange],
  );

  const handleDragStart = useCallback((idx: number) => setDragIndex(idx), []);
  const handleDragOver = useCallback(
    (e: React.DragEvent, idx: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === idx) return;
      const reordered = [...images];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(idx, 0, moved);
      onImagesChange(reordered);
      setDragIndex(idx);
    },
    [images, onImagesChange, dragIndex],
  );
  const handleDragEnd = useCallback(() => setDragIndex(null), []);

  const renderImageInput = () => {
    switch (inputMethod) {
      case 'upload':
        return (
          <div className={styles.uploadContainer} ref={uploadRef}>
            <KatFileUpload
              accept={validationSpec.acceptedFileTypes}
              onFilesAttached={handleFileAttached}
              onFilesRemoved={() => {}}
              onFilesAttachedError={handleFileAttachedError}
              hideClearAll
              multiple
              disabled={disabled || images.length >= maxImages}
              data-testid={`${dataTestId}-upload`}
            />
          </div>
        );
      case 'url':
        return (
          <div className={styles.urlInputRow}>
            <KatInput
              value={urlValue}
              onInput={(e) => setUrlValue((e.target as HTMLInputElement).value)}
              onKeydown={(e) => {
                if ('key' in e && e.key === 'Enter' && isValidUrl && !disabled && !isFetching) {
                  e.preventDefault();
                  handleUrlAdd();
                }
              }}
              placeholder="Paste image URL"
              disabled={disabled || isFetching || images.length >= maxImages}
              data-testid={`${dataTestId}-url-input`}
            />
            <KatButton
              variant="primary"
              onClick={handleUrlAdd}
              disabled={disabled || !isValidUrl || isFetching || images.length >= maxImages}
              data-testid={`${dataTestId}-url-fetch`}
            >
              {isFetching ? 'Fetching...' : 'Fetch'}
            </KatButton>
            <KatButton
              variant="warning"
              onClick={() => {
                setUrlValue('');
                setImageError(undefined);
              }}
              disabled={disabled || !urlValue.trim() || isFetching}
              data-testid={`${dataTestId}-url-clear`}
            >
              Clear
            </KatButton>
          </div>
        );
      case 'search':
        return (
          <SearchImageInput
            label="Search by Title or Keyword"
            placeholder="Enter movie or content name"
            onImageSelect={handleSearchSelect}
            selectedImageUrl=""
            onClearSelection={() => {}}
            disabled={disabled || images.length >= maxImages}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container} data-testid={dataTestId}>
      <label className={styles.sectionLabel}>
        Images ({images.length}/{maxImages})
      </label>

      {/* Image list */}
      {images.length > 0 && (
        <div className={styles.imageGrid}>
          {images.map((img, idx) => (
            <ImageRow
              key={img.id}
              img={img}
              idx={idx}
              isDragging={dragIndex === idx}
              isDisabled={disabled}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onRemove={handleRemoveImage}
            />
          ))}
        </div>
      )}
      {images.length > 1 && reorderHint && <p className={styles.hint}>{reorderHint}</p>}

      {/* Show input panel: directly for first image, or when Add Image is clicked */}
      {images.length > 0 && images.length < maxImages && !showImageInput && (
        <KatButton
          variant="secondary"
          onClick={() => setShowImageInput(true)}
          disabled={disabled}
          data-testid={`${dataTestId}-add-button`}
        >
          + Add Image
        </KatButton>
      )}

      {(images.length === 0 || showImageInput) && (
        <div className={styles.imageInputPanel}>
          <MaterialRadioButtonGroup
            name={`${dataTestId}-method`}
            options={INPUT_OPTIONS}
            value={inputMethod}
            onChange={(e) => setInputMethod(e.target.value as ImageInputMethod)}
            data-testid={`${dataTestId}-method-selector`}
            disabled={disabled}
          />
          {renderImageInput()}
          {imageError && <span className={styles.validationError}>{imageError}</span>}
          {images.length > 0 && (
            <KatButton
              variant="tertiary"
              onClick={() => setShowImageInput(false)}
              data-testid={`${dataTestId}-cancel-add`}
            >
              Cancel
            </KatButton>
          )}
        </div>
      )}
    </div>
  );
};
